// Chart of accounts — read-only (slice 1).
//
// Two jobs, and the second one drove the design:
//
//  1. SETUP CONFIRMATION. After creating a company the user is told "your company is set up" and
//     until now had no way to see it. Provisioning seeds the chart from the entity type's default
//     codes (measured: 86 accounts for a SA Private Company), so the visible TOTAL at the top of
//     this screen is the confirmation — that is why it is a first-class element, not a footnote.
//
//  2. IT IS THE TARGET SIDE OF A MAPPING. Legacy Sleek customers are the first clients, and their
//     import maps every OLD account onto a NEW one (source_code -> target_code). So the chart has
//     to be legible and FINDABLE BY CODE: typing "1200" or "debtors" lands instantly. The account
//     list, the search rule (`matchesAccount`) and the grouping (`groupByCategory`) are exported
//     as PURE functions precisely so the import-mapping UI can reuse them against the same
//     endpoint rather than growing a second, diverging list.
//
// THE WHOLE CHART IS LOADED ONCE. `GET /api/companies/{id}/accounts` returns everything — no
// pagination — so the search filters in memory: instant, no request per keystroke, and no async
// re-render that could yank the caret out of the box.
//
// THE SEARCH INPUT LIVES IN THE STATIC TEMPLATE, never inside a `${() => …}` reactive block. If it
// were reactive it would be destroyed and recreated on every keystroke and focus would be lost
// after one character. Only the count line and the RESULTS are reactive slots. test/chart.test.js
// asserts this by node identity and by document.activeElement, because it is invisible until a
// human tries to type.
import { html, signal, navigate } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { logout } from '../shared/auth.js';
import { theme, toggleTheme } from '../shared/theme.js';
import { icon } from '../shared/icons.js';
import { bottomNav, tenantNavItems } from '../shared/bottomnav.js';
import { demoBadge } from '../shared/demo.js';
import { t, initI18n } from '../shared/i18n.js';

// accounting.system_role — the 8 enum labels, in human words. A system-role account is one an
// import must never remap onto something else, so it is labelled on the row.
export const SYSTEM_ROLE_LABELS = {
  retained_earnings: 'Retained earnings',
  year_end_contra: 'Year-end contra',
  fx_gain_realised: 'FX gain (realised)',
  fx_loss_realised: 'FX loss (realised)',
  fx_gain_unrealised: 'FX gain (unrealised)',
  fx_loss_unrealised: 'FX loss (unrealised)',
  rounding: 'Rounding',
  suspense: 'Suspense',
};

// accounting.control_type — the 5 enum labels.
export const CONTROL_LABELS = {
  debtors: 'Debtors control', creditors: 'Creditors control', vat: 'VAT control',
  stock: 'Stock control', bank: 'Bank control',
};

// accounting.statement — set on the IFRS ROOT category; the API resolves the inheritance.
export const STATEMENT_LABELS = {
  financial_position: 'Financial position',
  comprehensive_income: 'Comprehensive income',
  changes_in_equity: 'Changes in equity',
  cash_flow: 'Cash flow',
};

// The search rule, in one place: an account matches on its CODE or its NAME, case-insensitively.
// Code first because that is the mapping key. Deliberately the same rule the API's `?q=` applies,
// so filtering in the browser and filtering on the server can never disagree.
export function matchesAccount(account, q) {
  const needle = (q || '').trim().toLowerCase();
  if (!needle) return true;
  return String(account.code || '').toLowerCase().includes(needle)
      || String(account.name || '').toLowerCase().includes(needle);
}

// Group into ledger categories, keeping the server's order (it sorts IFRS root → category →
// code). Keyed by category_code so a category can never appear as two separate blocks.
export function groupByCategory(accounts) {
  const groups = new Map();
  for (const account of accounts || []) {
    const key = account.category_code || '';
    if (!groups.has(key)) {
      groups.set(key, {
        code: key,
        name: account.category_name || 'Uncategorised',
        root: account.root_category_name || '',
        statement: account.statement || '',
        accounts: [],
      });
    }
    groups.get(key).accounts.push(account);
  }
  return [...groups.values()];
}

// Everything a mapping decision needs to see at a glance, on one line.
export function accountSubtitle(account) {
  const parts = [account.category_name];
  parts.push(account.normal_side === 'credit' ? 'Credit' : 'Debit');
  if (account.is_bank) parts.push('Bank account');
  if (account.control_type) parts.push(CONTROL_LABELS[account.control_type] || 'Control account');
  if (account.has_system_role) {
    parts.push(SYSTEM_ROLE_LABELS[account.system_role] || account.system_role);
  }
  if (!account.is_active) parts.push('Inactive');
  return parts.filter(Boolean).join(' · ');
}

// "86 accounts" with nothing typed — the setup confirmation — and "12 of 86 accounts" while a
// search is narrowing it, so the user always knows they are looking at a subset.
export function countLine(shown, total, searching) {
  if (searching) return `${shown} of ${total} accounts`;
  return `${total} account${total === 1 ? '' : 's'}`;
}

// `isDemo` is a SIGNAL, not a boolean: the company is resolved from /api/me after the first
// render, so the badge has to be able to appear when the answer arrives. (Same as import.js.)
function shell(title, body, companyId, isDemo) {
  const signOut = () => { logout(); navigate('/login'); };
  return html`
    <div class="main tenant-main">
      <header class="app-bar">
        <div class="brand-lockup"><span class="brand-dot"></span><span class="brand-name">aboo</span></div>
        <div class="app-title">${title}</div>
        ${() => isDemo && isDemo.value ? demoBadge() : null}
        <div class="app-user">
          <button class="theme-toggle" title=${t('common.toggle_theme', 'Toggle light / dark')} @click=${() => toggleTheme()}>${() => theme.value === 'dark' ? icon('sun', {size: 18}) : icon('moon', {size: 18})}</button>
          <button class="btn btn-sm btn-outline" @click=${() => navigate('/home')}>${() => t('common.home', 'Home')}</button>
        </div>
      </header>
      <div class="content">${body}</div>
      ${bottomNav(tenantNavItems({ companyId, onSignOut: signOut }), 'chart')}
    </div>`;
}

export function chartScreen(companyId) {
  initI18n(companyId);               // own shell (no tenantShell) → load the company's language bundle here
  const loading = signal(true);
  const err = signal('');
  const companyName = signal('');
  const isDemo = signal(false);
  const query = signal('');          // what is typed in the (static) search box
  const version = signal(0);         // bumps ONLY when the loaded chart changes

  // Plain (non-signal) state: the loaded chart. Read inside the reactive slots after `version` is
  // touched, so no keystroke can make the list itself reactive.
  let accounts = [];
  let chartTotal = 0;

  (async () => {
    try {
      const me = await api.get('/me');
      const company = (me.companies || []).find(c => String(c.id) === String(companyId));
      if (company) { companyName.value = company.name; isDemo.value = !!company.is_demo; }
    } catch { /* the chart request below is what decides whether this screen works */ }
  })();

  (async () => {
    try {
      // No page size, no ?per_page — the endpoint returns the WHOLE chart on purpose. A user
      // confirming their setup wants to see all of it.
      const result = await api.get(`/companies/${companyId}/accounts`);
      accounts = result.data || [];
      chartTotal = result.chart_total != null ? result.chart_total : accounts.length;
      if (result.company_name && !companyName.value) companyName.value = result.company_name;
      version.value++;
    } catch (e) {
      err.value = (e.data && e.data.error) || e.message || t('chart.err_load', 'Could not load your chart of accounts');
    }
    loading.value = false;
  })();

  const visible = () => accounts.filter(a => matchesAccount(a, query.value));

  const accountRow = a => html`
    <div class=${'row-card chart-row' + (a.is_active ? '' : ' inactive')
                  + (a.has_system_role ? ' chart-system' : '')}>
      <span class="chart-code">${a.code}</span>
      <span class="row-main">
        <b>${a.name}</b>
        <span class="row-sub">${accountSubtitle(a)}</span>
      </span>
      ${a.tax_code ? html`<span class="chart-vat mono" title=${(a.tax_name || '') + ' — default VAT code'}>${t('coa.vat', 'VAT')} ${a.tax_code}</span>` : ''}
    </div>`;

  const categoryGroup = g => html`
    <div class="chart-group">
      <div class="chart-group-head">
        <h2>${g.name}</h2>
        <span class="chart-group-root">${g.root}${STATEMENT_LABELS[g.statement] ? ' · ' + STATEMENT_LABELS[g.statement] : ''}</span>
        <span class="chart-group-n">${g.accounts.length}</span>
      </div>
      <div class="row-list">${g.accounts.map(accountRow)}</div>
    </div>`;

  // Reactive slot #1 — the count line. Reads its signals at the top, then plain values only.
  const count = () => {
    version.value; const q = query.value;
    if (loading.value) return '';
    return countLine(visible().length, chartTotal, !!q.trim());
  };

  // Reactive slot #2 — the RESULTS. This is the only thing a keystroke re-renders; the search
  // input itself is in the static template below and is never touched.
  const results = () => {
    version.value; const q = query.value;
    if (loading.value) return html`<div class="muted-block">${t('chart.loading', 'Loading your chart of accounts…')}</div>`;
    if (err.value) return null;
    if (!accounts.length) return html`<div class="muted-block">${t('chart.empty', 'This company has no accounts yet.')}</div>`;
    const shown = visible();
    if (!shown.length) {
      return html`<div class="muted-block">${t('chart.no_match_pre', 'No accounts match')} “${q}”. ${t('chart.no_match_post', 'Try a code like 1200, or part of a name.')}</div>`;
    }
    return html`<div class="chart-groups">${groupByCategory(shown).map(categoryGroup)}</div>`;
  };

  const content = html`
    <div class="page">
      <div class="page-head with-action">
        <div>
          <h1 class="page-title">${() => t('coa.title', 'Chart of accounts')}</h1>
          <div class="page-sub">${() => companyName.value || t('common.your_company', 'Your company')}</div>
        </div>
        <button class="btn btn-outline" title=${t('chart.import_hint', 'Bring a chart of accounts across from another system')}
                @click=${() => navigate('/companies/' + companyId + '/import')}>${() => t('common.import_data', 'Import data')}</button>
      </div>

      <div class="toolbar">
        <label class="visually-hidden" for="chart-search">${() => t('chart.search_label', 'Search accounts by code or name')}</label>
        <input class="form-control" id="chart-search" type="search" autocomplete="off"
               placeholder=${() => t('chart.search_placeholder', 'Search by code or name — e.g. 1200 or debtors')}
               @input=${e => { query.value = e.target.value; }}>
      </div>
      <div class="chart-count">${count}</div>

      ${results}

      ${() => err.value ? html`<div class="alert alert-danger py-2 mb-2">${err.value}</div>` : null}

      <div class="hint">${() => t('chart.hint', 'These are the accounts your company was set up with. Editing them — and mapping accounts from another system onto them — comes next.')}</div>
    </div>`;

  return shell(() => t('coa.title', 'Chart of accounts'), content, companyId, isDemo);
}
